import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '@services/api'
import { toast } from 'react-toastify'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const getStoredTokens = () => {
    const access = localStorage.getItem('accessToken')
    const refresh = localStorage.getItem('refreshToken')
    return { access, refresh }
  }

  const setStoredTokens = (access, refresh) => {
    localStorage.setItem('accessToken', access)
    localStorage.setItem('refreshToken', refresh)
  }

  const clearStoredTokens = () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('permissions')
  }

  const setStoredPermissions = (perms) => {
    localStorage.setItem('permissions', JSON.stringify(perms))
  }

  const getStoredPermissions = () => {
    const stored = localStorage.getItem('permissions')
    return stored ? JSON.parse(stored) : []
  }

  const fetchUserPermissions = useCallback(async () => {
    try {
      const response = await api.get('/auth/permissions/')
      const perms = response.data.permissions || []
      setPermissions(perms)
      setStoredPermissions(perms)
      return perms
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
      return []
    }
  }, [])

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await api.get('/auth/profile/')
      // Profile returns nested user object, flatten it for consistent access
      const profile = response.data
      const userData = {
        ...profile.user,
        profile: {
          id: profile.id,
          department: profile.department,
          department_name: profile.department_name,
          job_title: profile.job_title,
          phone_number: profile.phone_number,
          is_technician: profile.is_technician,
          roles: profile.roles,
          profile_picture: profile.profile_picture,
          profile_picture_url: profile.profile_picture_url,
        }
      }
      setUser(userData)
      // Also fetch permissions
      await fetchUserPermissions()
      return userData
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      clearStoredTokens()
      setUser(null)
      setPermissions([])
      return null
    }
  }, [fetchUserPermissions])

  const login = async (username, password, redirectUrl = null) => {
    try {
      const response = await api.post('/auth/login/', { username, password })
      const { access, refresh, user: userData, profile: profileData, permissions: userPerms } = response.data
      setStoredTokens(access, refresh)
      // Combine user and profile data for consistent structure
      const combinedUser = {
        ...userData,
        profile: profileData || null
      }
      setUser(combinedUser)
      // Set permissions from login response
      const perms = userPerms || []
      setPermissions(perms)
      setStoredPermissions(perms)
      toast.success('Login successful!')
      // Navigate to redirect URL if provided, otherwise to dashboard
      navigate(redirectUrl || '/dashboard')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const logout = useCallback(async () => {
    try {
      const { refresh } = getStoredTokens()
      if (refresh) {
        await api.post('/auth/logout/', { refresh })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearStoredTokens()
      setUser(null)
      setPermissions([])
      navigate('/login')
      toast.info('You have been logged out')
    }
  }, [navigate])

  const refreshToken = useCallback(async () => {
    try {
      const { refresh } = getStoredTokens()
      if (!refresh) {
        throw new Error('No refresh token')
      }
      const response = await api.post('/auth/refresh/', { refresh })
      const { access, refresh: newRefresh } = response.data
      setStoredTokens(access, newRefresh || refresh)
      return access
    } catch (error) {
      clearStoredTokens()
      setUser(null)
      setPermissions([])
      return null
    }
  }, [])

  // Permission checking functions
  const hasPermission = useCallback((permission) => {
    if (!user) return false
    // Superusers and staff have all permissions
    if (user.is_superuser || user.is_staff) return true
    return permissions.includes(permission)
  }, [user, permissions])

  const hasAnyPermission = useCallback((permissionList) => {
    if (!user) return false
    if (user.is_superuser || user.is_staff) return true
    return permissionList.some(p => permissions.includes(p))
  }, [user, permissions])

  const hasAllPermissions = useCallback((permissionList) => {
    if (!user) return false
    if (user.is_superuser || user.is_staff) return true
    return permissionList.every(p => permissions.includes(p))
  }, [user, permissions])

  // Check if user is admin (superuser or staff)
  const isAdmin = useMemo(() => {
    return user?.is_superuser || user?.is_staff || false
  }, [user])

  useEffect(() => {
    const initAuth = async () => {
      const { access } = getStoredTokens()
      if (access) {
        // Load cached permissions while fetching fresh ones
        const cachedPerms = getStoredPermissions()
        if (cachedPerms.length > 0) {
          setPermissions(cachedPerms)
        }
        await fetchUserProfile()
      }
      setLoading(false)
    }
    initAuth()
  }, [fetchUserProfile])

  // Periodic permission refresh (every 15 minutes while authenticated)
  useEffect(() => {
    if (!user) return

    const REFRESH_INTERVAL = 15 * 60 * 1000 // 15 minutes

    const interval = setInterval(() => {
      fetchUserPermissions()
    }, REFRESH_INTERVAL)

    return () => clearInterval(interval)
  }, [user, fetchUserPermissions])

  const value = {
    user,
    profile: user?.profile || null,
    permissions,
    loading,
    login,
    logout,
    refreshToken,
    fetchUserProfile,
    refreshProfile: fetchUserProfile, // Alias for backward compatibility
    fetchUserPermissions,
    isAuthenticated: !!user,
    isAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
